import React from 'react';
import { Box, Container, List, ListItemButton, ListItemIcon, ListItemText, Typography, Chip, SvgIcon, IconButton, Divider, Accordion, AccordionSummary, AccordionDetails, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Apps as AllIcon,
  Face as GirlsIcon,
  Boy as BoysIcon,
  Toys as ToysIcon,
  Stroller as StrollerIcon,
  Weekend as FurnitureIcon,
  BabyChangingStation as BabyIcon,
  Security as SafetyIcon,
  PregnantWoman as PregnantIcon,
  LocalDining as FeedingIcon,
  Bed as BedIcon,
  School as SchoolIcon,
  Category as OtherIcon,
  Checkroom as ClothingIcon,
  DirectionsRun as SportIcon,
  Favorite as OccasionIcon,
  Diamond as DesignerIcon,
  LocalMall as BagIcon,
  Spa as BeautyIcon,
  Man as ManIcon,
  Woman as WomanIcon,
  Hiking as HikingIcon,
  Watch as WatchIcon,
  Kitchen as KitchenIcon,
  BakeryDining as BakeryIcon,
  Restaurant as RestaurantIcon,
  Fastfood as FastfoodIcon,
  ElectricalServices as ElectricalIcon,
  DryCleaning as DryCleaningIcon,
  Home as HomeHouseIcon,
  Inventory2 as InventoryIcon,
  Celebration as CelebrationIcon,
  Build as BuildIcon,
  Yard as YardIcon,
  Pets as PetsIcon,
  SportsEsports as GamesIcon,
  Computer as ComputerIcon,
  PhoneIphone as PhoneIcon,
  Headphones as HeadphonesIcon,
  PhotoCamera as CameraIcon,
  TabletMac as TabletIcon,
  Tv as TvIcon,
  DevicesOther as DevicesIcon,
  MenuBook as BooksIcon,
  LibraryBooks as LibraryIcon,
  MusicNote as MusicIcon,
  Movie as MovieIcon,
  Style as StyleIcon,
  Casino as CasinoIcon,
  Extension as ExtensionIcon,
  ViewInAr as ViewInArIcon,
  CollectionsBookmark as CollectionsBookmarkIcon,
  AttachMoney as AttachMoneyIcon,
  LocalPostOffice as LocalPostOfficeIcon,
  MailOutline as MailOutlineIcon,
  Brush as BrushIcon,
  DirectionsBike as BikeIcon,
  FitnessCenter as FitnessIcon,
  Kayaking as KayakingIcon,
  SportsSoccer as SoccerIcon,
  SportsTennis as TennisIcon,
  SportsGolf as GolfIcon,
  Skateboarding as SkateboardingIcon,
  SportsMma as MmaIcon,
  SportsBaseball as BaseballIcon,
  DownhillSkiing as SkiIcon,
  EmojiNature as NatureIcon,
} from '@mui/icons-material';

export type MegaMenuItem = {
  label: string;
  icon?: React.ReactNode;
  key: string;
  children?: Array<{ label: string; path?: string }>;
};

export interface MegaMenuData {
  left: MegaMenuItem[];
  // Default columns (e.g., for "Alle anzeigen")
  right?: Array<{ title: string; items: Array<{ label: string; path?: string }> }>;
  // Specific columns per left key (e.g., clothing, shoes, beauty)
  rightByKey?: Record<string, Array<{ title: string; items: Array<{ label: string; path?: string }> }>>;
}

interface MegaMenuProps {
  open: boolean;
  category: string | null;
  data: Record<string, MegaMenuData>;
  onClose: () => void;
  onEnter?: () => void;
  secondaryHidden?: boolean;
  anchorTop?: number;
  onSelect?: (sel: { top: string; groupKey: string; columnTitle: string; label: string }) => void;
}

const MegaMenu: React.FC<MegaMenuProps> = ({ open, category, data, onClose, onEnter, secondaryHidden = false, anchorTop, onSelect }) => {
  const [selectedKey, setSelectedKey] = React.useState<string>('all');
  const [categoriesEnabled, setCategoriesEnabled] = React.useState<Record<string, boolean>>({});
  const [subcategoriesEnabled, setSubcategoriesEnabled] = React.useState<Record<string, Record<string, boolean>>>({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const offset = 55; // shift the desktop mega menu up by 55px

  React.useEffect(() => {
    // Reset selection when top-level category changes
    setSelectedKey('all');
  }, [category]);

  // Load website settings once
  React.useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await fetch('/website-settings.json', { cache: 'no-store' });
        if (!res.ok) return;
        const json = await res.json();
        setCategoriesEnabled(json?.categoriesEnabled || {});
        setSubcategoriesEnabled(json?.subcategoriesEnabled || {});
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('MegaMenu: failed to load website settings', e);
      }
    };
    loadSettings();
  }, []);

  if (!open || !category) return null;
  // Respect top-level enablement; if explicitly disabled, hide entirely
  if (Object.prototype.hasOwnProperty.call(categoriesEnabled, category) && !categoriesEnabled[category]) {
    return null;
  }
  const content = data[category] || data['default'];

  // Helper: is a sub label enabled? Defaults to true if not present
  const isSubEnabled = (top: string, label: string): boolean => {
    const m = subcategoriesEnabled?.[top];
    if (!m || typeof m[label] === 'undefined') return true;
    return !!m[label];
  };

  // Helper: does a left group have any enabled sub-items?
  const groupHasEnabled = (top: string, itemKey: string): boolean => {
    const cols = (content.rightByKey && content.rightByKey[itemKey]) || [];
    for (const col of cols) {
      for (const it of (col.items || [])) {
        const lbl = typeof it === 'string' ? it : it?.label || '';
        if (lbl && isSubEnabled(top, lbl)) return true;
      }
    }
    return false;
  };

  const filteredLeft = (content.left || []).filter((item) => groupHasEnabled(category, item.key));
  const rawColumns = (content.rightByKey && content.rightByKey[selectedKey]) || content.right || [];
  const columns = rawColumns
    .map((col: any) => ({
      ...col,
      items: (col.items || []).filter((sub: any) => {
        const lbl = typeof sub === 'string' ? sub : sub?.label || '';
        return lbl ? isSubEnabled(category, lbl) : false;
      }),
    }))
    .filter((col: any) => (col.items || []).length > 0);
  // Debug selection mapping in devtools to verify keys
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.debug('MegaMenu', { category, selectedKey, hasKey: !!content.rightByKey?.[selectedKey] });
  }

  return (
    <>
      {isMobile ? (
        <Box
          sx={{
            position: 'fixed',
            top: '84px',
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'background.paper',
            zIndex: (t) => t.zIndex.appBar - 2,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{category}</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', px: 1, py: 1 }}>
            {filteredLeft.map((item) => {
              const mobileCols = ((content.rightByKey && content.rightByKey[item.key]) || content.right || [])
                .map((col: any) => ({
                  ...col,
                  items: (col.items || []).filter((sub: any) => {
                    const lbl = typeof sub === 'string' ? sub : sub?.label || '';
                    return lbl ? isSubEnabled(category, lbl) : false;
                  }),
                }))
                .filter((col: any) => (col.items || []).length > 0);
              return (
                <Accordion key={item.key} disableGutters elevation={0} sx={{ '&::before': { display: 'none' }, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ py: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {item.icon && <Box sx={{ display: 'flex', alignItems: 'center' }}>{item.icon}</Box>}
                      <Typography sx={{ fontWeight: 600 }}>{item.label}</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0.5, pb: 1.5 }}>
                    {mobileCols.map((col, idx) => (
                      <Box key={`${item.key}-${idx}`} sx={{ mb: 1.25 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'text.secondary', fontWeight: 700 }}>
                          {col.title}
                        </Typography>
                        <List dense disablePadding>
                          {col.items.map((sub: any, i: number) => {
                            const label = typeof sub === 'string' ? sub : sub?.label || '';
                            return (
                              <ListItemButton
                                key={`${item.key}-${idx}-${i}`}
                                sx={{ borderRadius: 1, py: 1 }}
                                onClick={() => {
                                  if (onSelect && category) {
                                    onSelect({ top: category, groupKey: item.key, columnTitle: col.title, label });
                                  }
                                  onClose();
                                }}
                              >
                                <ListItemText primary={label} primaryTypographyProps={{ variant: 'body2' }} />
                              </ListItemButton>
                            );
                          })}
                        </List>
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            position: 'absolute',
            top: (anchorTop ?? 64) - offset,
            left: 0,
            right: 0,
            zIndex: (t) => t.zIndex.appBar - 1,
            bgcolor: 'background.paper',
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            border: '1px solid',
            borderColor: 'divider',
          }}
          onMouseLeave={onClose}
        >
          <Container maxWidth={false} sx={{ py: 2.5 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '280px 1fr' },
                gap: 2,
                height: '100%',
                minHeight: 0,
              }}
            >
              <Box
                sx={{
                  borderRight: { md: '1px solid' },
                  borderColor: 'divider',
                  pr: { md: 2 },
                  overflowY: 'auto',
                  maxHeight: { xs: 'auto', md: '70vh' },
                  '::-webkit-scrollbar': { width: 6 },
                  '::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.15)', borderRadius: 999 },
                }}
              >
                <List disablePadding>
                  {filteredLeft.map((item) => (
                    <ListItemButton
                      key={item.key}
                      onClick={() => setSelectedKey(item.key)}
                      onMouseEnter={() => setSelectedKey(item.key)}
                      sx={{
                        borderRadius: 1,
                        ...(selectedKey === item.key && { bgcolor: 'action.hover' }),
                      }}
                    >
                      {item.icon && <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>}
                      <ListItemText
                        primary={
                          <Typography variant="body1" sx={{ fontWeight: selectedKey === item.key ? 700 : 500 }}>
                            {item.label}
                          </Typography>
                        }
                      />
                      {item.children && item.children.length > 0 && (
                        <Chip size="small" label=">" sx={{ ml: 1 }} />
                      )}
                    </ListItemButton>
                  ))}
                </List>
              </Box>

              <Box sx={{ overflowY: 'auto', maxHeight: { xs: 'auto', md: '70vh' }, '::-webkit-scrollbar': { width: 6 }, '::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.15)', borderRadius: 999 } }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: { xs: 1, md: 2 } }}>
                  {columns.map((col, idx) => (
                    <Box key={idx}>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 700 }}>
                        {col.title}
                      </Typography>
                      <List dense disablePadding>
                        {col.items.map((sub: any, i: number) => {
                          const label = typeof sub === 'string' ? sub : sub?.label || '';
                          return (
                            <ListItemButton
                              key={`${col.title}-${i}`}
                              sx={{ borderRadius: 1 }}
                              onClick={() => {
                                if (onSelect && category) {
                                  onSelect({ top: category, groupKey: selectedKey, columnTitle: col.title, label });
                                }
                                onClose();
                              }}
                            >
                              <ListItemText primary={label} primaryTypographyProps={{ variant: 'body2' }} />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Container>
        </Box>
      )}
    </>
  );
}
;

const HorseIcon: React.FC<{ fontSize?: 'inherit' | 'large' | 'medium' | 'small' }> = ({ fontSize = 'small' }) => (
  <SvgIcon fontSize={fontSize} viewBox="0 0 24 24">
    <path d="M19 3l-3.5 1-3 3H9L4.5 9v3H9l2 2v3h2v-3l3-2V8l3-5zM8.5 10a1.5 1.5 0 110-3 1.5 1.5 0 010 3z" fill="currentColor" />
  </SvgIcon>
);

export const defaultMegaMenuData: Record<string, MegaMenuData> = {
  Damen: {
    left: [
      { key: 'all', label: 'Alle anzeigen', icon: <AllIcon fontSize="small" /> },
      { key: 'clothing', label: 'Kleidung', icon: <ClothingIcon fontSize="small" /> },
      { key: 'shoes', label: 'Schuhe', icon: <ClothingIcon fontSize="small" /> },
      { key: 'bags', label: 'Taschen', icon: <BagIcon fontSize="small" /> },
      { key: 'accessories', label: 'Accessoires', icon: <DesignerIcon fontSize="small" /> },
      { key: 'beauty', label: 'Beauty', icon: <BeautyIcon fontSize="small" /> },
    ],
    right: [
      {
        title: 'Kleidung',
        items: [
          { label: 'Alle anzeigen' },
          { label: 'Pullover & Strickpullover' },
          { label: 'Kleider' },
          { label: 'Tops & T-Shirts' },
          { label: 'Hosen & Leggings' },
          { label: 'Jumpsuits & Playsuits' },
          { label: 'Unterwäsche & Nachtwäsche' },
          { label: 'Activewear' },
          { label: 'Sonstiges' },
        ],
      },
      {
        title: 'Weitere Kleidung',
        items: [
          { label: 'Jacken & Mäntel' },
          { label: 'Blazer & Anzüge' },
          { label: 'Röcke' },
          { label: 'Jeans' },
          { label: 'Shorts' },
          { label: 'Bademode' },
          { label: 'Umstandskleidung' },
          { label: 'Kostüme & Besonderes' },
        ],
      },
      {
        title: 'Schuhe',
        items: [
          { label: 'Alle anzeigen' },
          { label: 'Ballerinas' },
          { label: 'Bootsschuhe, Loafer & Mokassins' },
          { label: 'Clogs' },
          { label: 'Zehentrenner & Slides' },
          { label: 'Schnürschuhe' },
          { label: 'Sandalen' },
          { label: 'Sportschuhe' },
          { label: 'Stiefel' },
          { label: 'Espadrilles' },
          { label: 'Absatzschuhe' },
          { label: 'Spangenschuhe & Schuhe mit T-Riemen' },
          { label: 'Hausschuhe, Pantoffeln & Slipper' },
          { label: 'Sneaker' },
        ],
      },
      {
        title: 'Accessoires',
        items: [
          { label: 'Alle anzeigen' },
          { label: 'Gürtel' },
          { label: 'Haarschmuck' },
          { label: 'Hüte & Mützen' },
          { label: 'Schlüsselanhänger' },
          { label: 'Sonnenbrillen' },
          { label: 'Uhren' },
          { label: 'Bandanas & Kopftücher' },
          { label: 'Handschuhe' },
          { label: 'Halstücher' },
          { label: 'Schmuck' },
          { label: 'Tücher & Schals' },
          { label: 'Regenschirme' },
          { label: 'Sonstige Accessoires' },
        ],
      },
    ],
    rightByKey: {
      all: [
        {
          title: 'Kleidung',
          items: [
            { label: 'Alle anzeigen' },
            { label: 'Pullover & Strickpullover' },
            { label: 'Kleider' },
            { label: 'Tops & T-Shirts' },
            { label: 'Hosen & Leggings' },
            { label: 'Jumpsuits & Playsuits' },
            { label: 'Unterwäsche & Nachtwäsche' },
            { label: 'Activewear' },
            { label: 'Sonstiges' },
          ],
        },
        {
          title: 'Weitere Kleidung',
          items: [
            { label: 'Jacken & Mäntel' },
            { label: 'Blazer & Anzüge' },
            { label: 'Röcke' },
            { label: 'Jeans' },
            { label: 'Shorts' },
            { label: 'Bademode' },
            { label: 'Umstandskleidung' },
            { label: 'Kostüme & Besonderes' },
          ],
        },
        {
          title: 'Accessoires',
          items: [
            { label: 'Alle anzeigen' },
            { label: 'Gürtel' },
            { label: 'Haarschmuck' },
            { label: 'Hüte & Mützen' },
            { label: 'Schlüsselanhänger' },
            { label: 'Sonnenbrillen' },
            { label: 'Uhren' },
            { label: 'Bandanas & Kopftücher' },
            { label: 'Handschuhe' },
            { label: 'Halstücher' },
            { label: 'Schmuck' },
            { label: 'Tücher & Schals' },
            { label: 'Regenschirme' },
            { label: 'Sonstige Accessoires' },
          ],
        },
      ],
      clothing: [
        {
          title: 'Kleidung',
          items: [
            { label: 'Alle anzeigen' },
            { label: 'Pullover & Strickpullover' },
            { label: 'Kleider' },
            { label: 'Tops & T-Shirts' },
            { label: 'Hosen & Leggings' },
            { label: 'Jumpsuits & Playsuits' },
            { label: 'Unterwäsche & Nachtwäsche' },
            { label: 'Activewear' },
            { label: 'Sonstiges' },
          ],
        },
        {
          title: 'Weitere Kleidung',
          items: [
            { label: 'Jacken & Mäntel' },
            { label: 'Blazer & Anzüge' },
            { label: 'Röcke' },
            { label: 'Jeans' },
            { label: 'Shorts' },
            { label: 'Bademode' },
            { label: 'Umstandskleidung' },
            { label: 'Kostüme & Besonderes' },
          ],
        },
      ],
      shoes: [
        {
          title: 'Schuhe',
          items: [
            { label: 'Alle anzeigen' },
            { label: 'Bootsschuhe, Loafer & Mokassins' },
            { label: 'Clogs' },
            { label: 'Zehentrenner & Slides' },
            { label: 'Schnürschuhe' },
            { label: 'Sandalen' },
            { label: 'Sportschuhe' },
          ],
        },
        {
          title: 'Weitere Schuhe',
          items: [
            { label: 'Ballerinas' },
            { label: 'Stiefel' },
            { label: 'Espadrilles' },
            { label: 'Absatzschuhe' },
            { label: 'Spangenschuhe & Schuhe mit T-Riemen' },
            { label: 'Hausschuhe, Pantoffeln & Slipper' },
            { label: 'Sneaker' },
          ],
        },
      ],
      bags: [
        {
          title: 'Taschen',
          items: [
            { label: 'Alle anzeigen' },
            { label: 'Strandtaschen' },
            { label: 'Bucket Bags' },
            { label: 'Clutches' },
            { label: 'Sporttaschen' },
            { label: 'Hobo Bags' },
            { label: 'Reisetaschen' },
            { label: 'Satchels & Messenger Bags' },
            { label: 'Tragetaschen' },
            { label: 'Wristlets' },
          ],
        },
        {
          title: 'Weitere Taschen',
          items: [
            { label: 'Rucksäcke' },
            { label: 'Aktentaschen' },
            { label: 'Kleiderhüllen' },
            { label: 'Handtaschen' },
            { label: 'Kosmetiktaschen' },
            { label: 'Umhängetaschen' },
            { label: 'Portemonnaies' },
          ],
        },
      ],
      accessories: [
        {
          title: 'Accessoires',
          items: [
            { label: 'Alle anzeigen' },
            { label: 'Gürtel' },
            { label: 'Haarschmuck' },
            { label: 'Hüte & Mützen' },
            { label: 'Schlüsselanhänger' },
            { label: 'Sonnenbrillen' },
            { label: 'Uhren' },
          ],
        },
        {
          title: 'Weitere Accessoires',
          items: [
            { label: 'Bandanas & Kopftücher' },
            { label: 'Handschuhe' },
            { label: 'Halstücher' },
            { label: 'Schmuck' },
            { label: 'Tücher & Schals' },
            { label: 'Regenschirme' },
            { label: 'Sonstige Accessoires' },
          ],
        },
      ],
      beauty: [
        {
          title: 'Beauty',
          items: [
            { label: 'Alle anzeigen' },
            { label: 'Parfums' },
            { label: 'Beauty-Zubehör' },
            { label: 'Nagelpflege' },
            { label: 'Haarpflege' },
          ],
        },
        {
          title: 'Pflege & Mehr',
          items: [
            { label: 'Make-up' },
            { label: 'Gesichtspflege' },
            { label: 'Handpflege' },
            { label: 'Körperpflege' },
            { label: 'Sonstiges' },
          ],
        },
      ],
    },
  },
  Kinder: {
    left: [
      { key: 'all', label: 'Alle anzeigen', icon: <AllIcon fontSize="small" /> },
      { key: 'girls', label: 'Mädchen', icon: <GirlsIcon fontSize="small" />, children: [{ label: 'Mehr' }] },
      { key: 'boys', label: 'Jungs', icon: <BoysIcon fontSize="small" /> },
      { key: 'toys', label: 'Spielzeug', icon: <ToysIcon fontSize="small" /> },
      { key: 'strollers', label: 'Kinderwagen, Tragen & Autositze', icon: <StrollerIcon fontSize="small" /> },
      { key: 'furniture', label: 'Möbel & Deko', icon: <FurnitureIcon fontSize="small" /> },
      { key: 'baby', label: 'Baden & Wickeln', icon: <BabyIcon fontSize="small" /> },
      { key: 'safety', label: 'Kindersicherung & Sicherheitsausstattung', icon: <SafetyIcon fontSize="small" /> },
      { key: 'pregnancy', label: 'Gesundheit & Schwangerschaft', icon: <PregnantIcon fontSize="small" /> },
      { key: 'feeding', label: 'Stillen & Füttern', icon: <FeedingIcon fontSize="small" /> },
      { key: 'bed', label: 'Schlafen & Bettzeug', icon: <BedIcon fontSize="small" /> },
      { key: 'school', label: 'Schulbedarf', icon: <SchoolIcon fontSize="small" /> },
      { key: 'other', label: 'Sonstige Artikel für Kinder', icon: <OtherIcon fontSize="small" /> },
    ],
    rightByKey: {
      girls: [
        {
          title: 'Mädchen',
          items: [
            { label: 'Alle anzeigen' },
            { label: 'Schuhe' },
            { label: 'Pullover und Jäckchen' },
            { label: 'Kleider' },
            { label: 'Hosen & Shorts' },
            { label: 'Accessoires' },
            { label: 'Unterwäsche & Socken' },
            { label: 'Sportkleidung' },
            { label: 'Für Zwillinge' },
            { label: 'Für besondere Anlässe' },
          ],
        },
        {
          title: 'Weitere Kategorien',
          items: [
            { label: 'Babykleidung (Mädchen)' },
            { label: 'Outerwear' },
            { label: 'Shirts, Tops & Blusen' },
            { label: 'Röcke' },
            { label: 'Kinder-Handtaschen' },
            { label: 'Badebekleidung' },
            { label: 'Nachtwäsche' },
            { label: 'Sets & Kleidungspakete' },
            { label: 'Schicke Kleider & Kostüme' },
            { label: 'Mehr' },
          ],
        },
      ],
      boys: [
        {
          title: 'Jungs',
          items: [
            { label: 'Alle anzeigen' },
            { label: 'Schuhe' },
            { label: 'Pullover & Jäckchen' },
            { label: 'Hosen & Shorts' },
            { label: 'Accessoires' },
            { label: 'Unterwäsche' },
            { label: 'Sportartikel' },
            { label: 'Für Zwillinge' },
            { label: 'Für besondere Anlässe' },
          ],
        },
        {
          title: 'Weitere Kategorien',
          items: [
            { label: 'Babykleidung (Jungs)' },
            { label: 'Outerwear' },
            { label: 'Shirts, Tops & Hemden' },
            { label: 'Kinder-Handtaschen' },
            { label: 'Badebekleidung' },
            { label: 'Nachtwäsche' },
            { label: 'Sets & Kleidungspakete' },
            { label: 'Kostüme' },
            { label: 'Sonstige' },
          ],
        },
      ],
      toys: [
        {
          title: 'Spielzeug',
          items: [
            { label: 'Alle anzeigen' },
            { label: 'Kunst & Basteln' },
            { label: 'Klötze & Bauspielzeug' },
            { label: 'Verkleidungs- & Rollenspiele' },
            { label: 'Elektronisches Spielzeug' },
            { label: 'Fidget Toys, Zirkus- & Scherzspielzeug' },
            { label: 'Stofftiere' },
          ],
        },
        {
          title: 'Weitere Spielzeuge',
          items: [
            { label: 'Figuren & Zubehör' },
            { label: 'Aktivitätsspiele & Spielzeug für Babys' },
            { label: 'Puppen & Zubehör' },
            { label: 'Lernspielzeug' },
            { label: 'Instrumente & Musik-Spielzeuge' },
            { label: 'Outdoor- & Sportspielzeug' },
            { label: 'Autos, Züge & sonstige Fahrzeuge' },
          ],
        },
      ],
      strollers: [
        {
          title: 'Kinderwagen, Tragen & Autositze',
          items: [
            { label: 'Alle anzeigen' },
            { label: 'Kinderwagen' },
            { label: 'Autositze' },
            { label: 'Autositz-Zubehör' },
          ],
        },
        {
          title: 'Weitere',
          items: [
            { label: 'Tragehilfen' },
            { label: 'Kinderwagen-Zubehör' },
            { label: 'Kindersitzerhöhung' },
          ],
        },
      ],
      furniture: [
        {
          title: 'Möbel & Deko',
          items: [
            { label: 'Alle anzeigen' },
            { label: 'Spielmatten & -böden' },
            { label: 'Polster & Nester' },
            { label: 'Kinderzimmer-Möbel' },
            { label: 'Stühle' },
            { label: 'Regale' },
            { label: 'Kleiderschränke' },
          ],
        },
        {
          title: 'Weitere Möbel & Deko',
          items: [
            { label: 'Matratzen' },
            { label: 'Laufgitter' },
            { label: 'Deko & Andenken' },
            { label: 'Teppiche und Läufer' },
            { label: 'Möbel für Spiel & Spaß' },
            { label: '(Schreib-)Tische' },
          ],
        },
      ],
      baby: [
        {
          title: 'Baden & Wickeln',
          items: [
            { label: 'Alle anzeigen' },
            { label: 'Baden' },
            { label: 'Windeln' },
            { label: 'Töpfchen' },
            { label: 'Tritthocker' },
          ],
        },
        {
          title: 'Wickeln & Pflege',
          items: [
            { label: 'Wickeltaschen' },
            { label: 'Wickelunterlagen & Bezüge' },
            { label: 'Windelaufbewahrung und -entsorgung' },
            { label: 'Hautpflege & Hygiene' },
          ],
        },
      ],
      safety: [
        {
          title: 'Kindersicherung & Sicherheit',
          items: [
            { label: 'Alle anzeigen' },
            { label: 'Zubehör für die Kindersicherung' },
            { label: 'Sicherheitsgurte' },
          ],
        },
        {
          title: 'Weitere Sicherheit',
          items: [
            { label: 'Schutzgitter' },
            { label: 'Hörschutz' },
          ],
        },
      ],
      pregnancy: [
        {
          title: 'Gesundheit & Schwangerschaft',
          items: [
            { label: 'Alle anzeigen' },
            { label: 'Nasensauger' },
            { label: 'Schwangerschaftskissen' },
            { label: 'Waagen' },
          ],
        },
        {
          title: 'Weitere Gesundheit',
          items: [
            { label: 'Luftbefeuchter' },
            { label: 'Schwangerschaftsnachsorge' },
            { label: 'Schwangerschaftsgurte' },
            { label: 'Thermometer' },
          ],
        },
      ],
      feeding: [
        {
          title: 'Stillen & Füttern',
          items: [
            { label: 'Alle anzeigen' },
            { label: 'Lätzchen' },
            { label: 'Stillen' },
            { label: 'Stillkissen & Bezüge' },
            { label: 'Schnuller-Zubehör' },
            { label: 'Hochstuhl-Zubehör' },
            { label: 'Sterilisatoren' },
          ],
        },
        {
          title: 'Weitere Still-/Fütterartikel',
          items: [
            { label: 'Mixer & Zubereiter' },
            { label: 'Flaschenfütterung' },
            { label: 'Geschirr & Besteck' },
            { label: 'Schnuller' },
            { label: 'Hochstühle' },
            { label: 'Mull- und Spucktücher' },
          ],
        },
      ],
      bed: [
        {
          title: 'Schlafen & Bettzeug',
          items: [
            { label: 'Alle anzeigen' },
            { label: 'Schutzvorrichtungen fürs Bett' },
            { label: 'Verdunklungsrollos' },
            { label: 'Nachtlichter & Lichtwecker' },
            { label: 'Schlafsäcke' },
            { label: 'Klangmaschinen' },
          ],
        },
        {
          title: 'Weitere Schlafartikel',
          items: [
            { label: 'Babymonitore' },
            { label: 'Bettzeug, Decken & Überwürfe' },
            { label: 'Wärmeauflagen & Wärmflaschen' },
            { label: 'Schlafsäcke & Decken mit Ärmeln' },
            { label: 'Pucktücher' },
          ],
        },
      ],
      school: [
        {
          title: 'Schulbedarf',
          items: [
            { label: 'Alle anzeigen' },
            { label: 'Schultaschen' },
          ],
        },
        {
          title: 'Weitere Schulartikel',
          items: [
            { label: 'Lunchboxen & -taschen' },
            { label: 'Schulbedarf' },
          ],
        },
      ],
    },
    right: [
      {
        title: 'Alle anzeigen',
        items: [
          { label: 'Babybekleidung (Mädchen)' },
          { label: 'Outerwear' },
          { label: 'Shirts, Tops & Blusen' },
          { label: 'Kleider' },
          { label: 'Hosen & Shorts' },
          { label: 'Accessoires' },
          { label: 'Unterwäsche & Socken' },
          { label: 'Sportkleidung' },
          { label: 'Für Zwillinge' },
          { label: 'Für besondere Anlässe' },
          { label: 'Mehr' },
        ],
      },
      {
        title: 'Schuhe',
        items: [
          { label: 'Sneaker' },
          { label: 'Sandalen' },
          { label: 'Stiefel' },
          { label: 'Hausschuhe' },
        ],
      },
      {
        title: 'Pullover und Jäckchen',
        items: [
          { label: 'Strick' },
          { label: 'Cardigans' },
          { label: 'Hoodies' },
        ],
      },
      {
        title: 'Kleider',
        items: [
          { label: 'Sommerkleider' },
          { label: 'Partykleider' },
          { label: 'Schicke Kleider & Kostüme' },
        ],
      },
      {
        title: 'Designer',
        items: [
          { label: 'Premium Marken' },
          { label: 'Sets & Kleidungspakete' },
        ],
      },
    ],
  },
  Herren: {
    left: [
      { key: 'all', label: 'Alle anzeigen', icon: <AllIcon fontSize="small" /> },
      { key: 'clothing', label: 'Kleidung', icon: <ClothingIcon fontSize="small" /> },
      { key: 'shoes', label: 'Schuhe', icon: <HikingIcon fontSize="small" /> },
      { key: 'accessories', label: 'Accessoires', icon: <WatchIcon fontSize="small" /> },
      { key: 'care', label: 'Körper- & Gesichtspflege', icon: <BeautyIcon fontSize="small" /> },
    ],
    rightByKey: {
      clothing: [
        {
          title: 'Kleidung',
          items: [
            { label: 'Alle anzeigen' },
            { label: 'Jacken & Mäntel' },
            { label: 'Anzüge & Blazer' },
            { label: 'Hosen' },
            { label: 'Unterwäsche & Socken' },
            { label: 'Badebekleidung' },
            { label: 'Spezielle Kleidung' },
          ],
        },
        {
          title: 'Weitere Kleidung',
          items: [
            { label: 'Jeans' },
            { label: 'Tops & T-Shirts' },
            { label: 'Pullover & Sweater' },
            { label: 'Shorts' },
            { label: 'Nachtwäsche' },
            { label: 'Sportartikel' },
            { label: 'Sonstiges' },
          ],
        },
      ],
      shoes: [
        {
          title: 'Schuhe',
          items: [
            { label: 'Alle anzeigen' },
            { label: 'Sneaker' },
            { label: 'Bootsschuhe / Loafer / Mokassins' },
            { label: 'Clogs' },
            { label: 'Zehentrenner & Slides' },
            { label: 'Schnürschuhe' },
            { label: 'Sandalen' },
            { label: 'Sportschuhe' },
          ],
        },
        {
          title: 'Weitere Schuhe',
          items: [
            { label: 'Stiefel' },
            { label: 'Derby & Oxfords' },
            { label: 'Slipper' },
            { label: 'Hausschuhe / Pantoffeln' },
            { label: 'Trekking- & Wanderschuhe' },
            { label: 'Arbeitsschuhe' },
            { label: 'Espadrilles' },
          ],
        },
      ],
      accessories: [
        {
          title: 'Accessoires',
          items: [
            { label: 'Alle anzeigen' },
            { label: 'Bandanas & Kopftücher' },
            { label: 'Hosenträger' },
            { label: 'Halstücher' },
            { label: 'Schmuck' },
            { label: 'Tücher & Schals' },
            { label: 'Krawatten & Fliegen' },
          ],
        },
        {
          title: 'Weitere Accessoires',
          items: [
            { label: 'Taschen & Rucksäcke' },
            { label: 'Gürtel' },
            { label: 'Handschuhe' },
            { label: 'Hüte & Mützen' },
            { label: 'Einstecktücher' },
            { label: 'Sonnenbrillen' },
          ],
        },
      ],
      care: [
        {
          title: 'Pflege',
          items: [
            { label: 'Alle anzeigen' },
            { label: 'Gesichtspflege-Zubehör' },
            { label: 'Körperpflege' },
            { label: 'Parfums' },
            { label: 'Sets' },
          ],
        },
        {
          title: 'Weitere Pflege',
          items: [
            { label: 'Gesichtspflege' },
            { label: 'Haarpflege' },
            { label: 'Hand- & Nagelpflege' },
            { label: 'Make-up' },
            { label: 'Andere Pflegeartikel' },
          ],
        },
      ],
    },
    right: [
      {
        title: 'Alle Kategorien',
        items: [
          { label: 'Alle anzeigen' },
          { label: 'Kleidung' },
          { label: 'Schuhe' },
          { label: 'Accessoires' },
          { label: 'Körper- & Gesichtspflege' },
        ],
      },
    ],
  },
  Designerartikel: {
    left: [
      { key: 'all', label: 'Alle anzeigen', icon: <AllIcon fontSize="small" /> },
      { key: 'women', label: 'Designerartikel für Damen', icon: <WomanIcon fontSize="small" /> },
      { key: 'men', label: 'Designerartikel für Herren', icon: <ManIcon fontSize="small" /> },
    ],
    rightByKey: {
      women: [
        {
          title: 'Für Damen',
          items: [
            { label: 'Alle anzeigen' },
            { label: 'Designerschuhe' },
            { label: 'Designerkleidung' },
          ],
        },
        {
          title: 'Weitere Kategorien',
          items: [
            { label: 'Designertaschen' },
            { label: 'Designer-Accessoires' },
          ],
        },
      ],
      men: [
        {
          title: 'Für Herren',
          items: [
            { label: 'Alle anzeigen' },
            { label: 'Designer-Accessoires' },
          ],
        },
        {
          title: 'Weitere Kategorien',
          items: [
            { label: 'Designerschuhe' },
            { label: 'Designerkleidung' },
          ],
        },
      ],
    },
    right: [
      {
        title: 'Designerartikel',
        items: [
          { label: 'Alle anzeigen' },
          { label: 'Designerschuhe' },
          { label: 'Designerkleidung' },
        ],
      },
      {
        title: 'Mehr',
        items: [
          { label: 'Designertaschen' },
          { label: 'Designer-Accessoires' },
        ],
      },
    ],
  },
  Elektronik: {
    left: [
      { key: 'all', label: 'Alle anzeigen', icon: <AllIcon fontSize="small" /> },
      { key: 'games', label: 'Videospiele & Konsolen', icon: <GamesIcon fontSize="small" /> },
      { key: 'computers', label: 'Computer & Zubehör', icon: <ComputerIcon fontSize="small" /> },
      { key: 'mobile', label: 'Mobiltelefone & Kommunikation', icon: <PhoneIcon fontSize="small" /> },
      { key: 'audio', label: 'Audio, Kopfhörer & Hi-Fi', icon: <HeadphonesIcon fontSize="small" /> },
      { key: 'cameras', label: 'Kameras & Zubehör', icon: <CameraIcon fontSize="small" /> },
      { key: 'tablets', label: 'Tablets, E-Reader & Zubehör', icon: <TabletIcon fontSize="small" /> },
      { key: 'tv', label: 'TV & Heimkino', icon: <TvIcon fontSize="small" /> },
      { key: 'beauty_electronics', label: 'Elektronik für Beauty & Körperpflege', icon: <BeautyIcon fontSize="small" /> },
      { key: 'wearables', label: 'Wearables', icon: <WatchIcon fontSize="small" /> },
      { key: 'other_devices', label: 'Andere Geräte & Zubehör', icon: <DevicesIcon fontSize="small" /> },
    ],
    rightByKey: {
      games: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Spiele' },
            { label: 'Gaming-Headsets' },
            { label: 'Virtual-Reality-Geräte' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Spielkonsolen' },
            { label: 'Controller' },
            { label: 'Simulatoren' },
            { label: 'Zubehör' },
          ],
        },
      ],
      computers: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Desktop-Computer' },
            { label: 'Leermedien' },
            { label: 'Laptop-Zubehör' },
            { label: 'Tastaturen & Zubehör' },
            { label: 'Mauspads' },
            { label: 'Computer-Lautsprecher' },
            { label: 'Webcams' },
            { label: 'Drucker & Zubehör' },
            { label: 'Touch- & Grafik-Pads' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Laptops' },
            { label: 'Computerteile und -komponenten' },
            { label: 'Computer-Zubehör' },
            { label: 'Docking-Stationen & USB-Hubs' },
            { label: 'Mäuse' },
            { label: 'Monitore & Zubehör' },
            { label: 'Computer-Mikrofone' },
            { label: 'Netzwerkgeräte' },
            { label: 'Scanner & Zubehör' },
          ],
        },
      ],
      audio: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Tragbare Musik-Player' },
            { label: 'Tragbare Lautsprecher' },
            { label: 'Audiosysteme für Zuhause' },
            { label: 'Teile für Audio- und Hi-Fi-Geräte für Zuhause' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Over-Ear- & In-Ear-Kopfhörer' },
            { label: 'Tragbare Radios' },
            { label: 'Smarte Lautsprecher' },
            { label: 'Zubehör für Audiogeräte' },
          ],
        },
      ],
      mobile: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Bauteile & Zubehör für Mobiltelefone' },
            { label: 'Faxgeräte' },
            { label: 'Mobiltelefon-Attrappen' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Mobiltelefone' },
            { label: 'Festnetztelefone' },
            { label: 'Rundfunk' },
          ],
        },
      ],
      tablets: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'E-Reader' },
            { label: 'PDAs' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Tablets' },
            { label: 'Digitale Notizbücher' },
            { label: 'Zubehör' },
          ],
        },
      ],
      cameras: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Objektive' },
            { label: 'Speicherkarten' },
            { label: 'Stabilisatoren & Halterungen' },
            { label: 'Fotostudio-Ausstattung' },
            { label: 'Zubehör' },
            { label: 'Sonstige Fotografie-Ausstattung' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Kameras' },
            { label: 'Blitzgeräte' },
            { label: 'Stative' },
            { label: 'Dunkelkammer-Ausstattung' },
            { label: 'Kameradrohnen & Zubehör' },
            { label: 'Kamera-Ersatzteile' },
          ],
        },
      ],
      other_devices: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'GPS & Satelliten-Navigationsgeräte' },
            { label: 'Gepäckwaagen' },
            { label: 'Kabel' },
            { label: 'Powerbanks' },
            { label: 'Batterien & Netzteile' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: '3D-Druck & -Scannen' },
            { label: 'Schlüsselfinder' },
            { label: 'Adapter' },
            { label: 'Ladegeräte' },
            { label: 'Überspannungsschutz & Steckdosenleisten' },
            { label: 'Sonstiges Zubehör' },
          ],
        },
      ],
      wearables: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Fitness-Tracker' },
            { label: 'Smartrings' },
            { label: 'Smartwatch-Hüllen' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Smartwatches' },
            { label: 'Smartglasses' },
            { label: 'Ersatzbänder' },
          ],
        },
      ],
      beauty_electronics: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Beauty-Tools' },
            { label: 'Massage-Tools' },
            { label: 'Nagelpflege-Tools' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Hairstyling-Tools' },
            { label: 'Rasur & Haarentfernung' },
            { label: 'Elektrische Zahn- & Mundpflege' },
            { label: 'Personenwaage' },
          ],
        },
      ],
      tv: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Beamer' },
            { label: 'Fernsehantennen' },
            { label: 'Videodecoder' },
            { label: 'Heimkino-Systeme' },
            { label: 'DVD-Player' },
            { label: 'Andere Video-Player' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Fernseher' },
            { label: 'Streaming-Geräte' },
            { label: 'Satellitenschüsseln' },
            { label: 'TV-Receiver' },
            { label: 'Blu-ray-Player' },
            { label: 'VHS-Rekorder' },
            { label: 'Zubehör für TV & Heimkino' },
          ],
        },
      ],
    },
    right: [
      {
        title: 'Alle Kategorien',
        items: [
          { label: 'Videospiele & Konsolen' },
          { label: 'Computer & Zubehör' },
          { label: 'Mobiltelefone & Kommunikation' },
        ],
      },
    ],
  },
  Unterhaltung: {
    left: [
      { key: 'all', label: 'Alle anzeigen', icon: <AllIcon fontSize="small" /> },
      { key: 'books', label: 'Bücher', icon: <BooksIcon fontSize="small" /> },
      { key: 'magazines', label: 'Zeitschriften', icon: <LibraryIcon fontSize="small" /> },
      { key: 'music', label: 'Musik', icon: <MusicIcon fontSize="small" /> },
      { key: 'video', label: 'Video', icon: <MovieIcon fontSize="small" /> },
    ],
    rightByKey: {
      books: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Sachbücher' },
            { label: 'Comics, Mangas & Graphic Novels' },
            { label: 'Mal-, Rätsel- & interaktive Bücher' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Belletristik' },
            { label: 'Kinder & junge Erwachsene' },
            { label: 'Lehrbücher & Lernmaterialien' },
          ],
        },
      ],
      video: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Betamax' },
            { label: 'DVD' },
            { label: 'LaserDisc' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: '4K Blu-ray' },
            { label: 'Blu-ray' },
            { label: 'HD DVD' },
            { label: 'VHS' },
          ],
        },
      ],
      music: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'CDs' },
            { label: 'Schallplatten' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Audiokassetten' },
            { label: 'MiniDiscs' },
          ],
        },
      ],
      magazines: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Mode & Lifestyle' },
            { label: 'Haus & Garten' },
            { label: 'Kinder & Jugend' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Auto & Motorrad' },
            { label: 'Wissenschaft & Technik' },
            { label: 'Sport' },
            { label: 'Kunst & Kultur' },
          ],
        },
      ],
    },
    right: [
      {
        title: 'Alle Kategorien',
        items: [
          { label: 'Bücher' },
          { label: 'Zeitschriften' },
          { label: 'Musik' },
          { label: 'Video' },
        ],
      },
    ],
  },
  'Hobby- & Sammlerartikel': {
    left: [
      { key: 'all', label: 'Alle anzeigen', icon: <AllIcon fontSize="small" /> },
      { key: 'cards', label: 'Sammelkarten', icon: <StyleIcon fontSize="small" /> },
      { key: 'boardgames', label: 'Brettspiele', icon: <CasinoIcon fontSize="small" /> },
      { key: 'puzzles', label: 'Puzzles', icon: <ExtensionIcon fontSize="small" /> },
      { key: 'tabletop', label: 'Tabletop- & Miniaturspiele', icon: <ViewInArIcon fontSize="small" /> },
      { key: 'memorabilia', label: 'Erinnerungsstücke', icon: <CollectionsBookmarkIcon fontSize="small" /> },
      { key: 'coins', label: 'Münzen & Geldscheine', icon: <AttachMoneyIcon fontSize="small" /> },
      { key: 'stamps', label: 'Briefmarken', icon: <LocalPostOfficeIcon fontSize="small" /> },
      { key: 'postcards', label: 'Postkarten', icon: <MailOutlineIcon fontSize="small" /> },
      { key: 'music_instruments', label: 'Musikinstrumente & -ausrüstung', icon: <MusicIcon fontSize="small" /> },
      { key: 'crafts', label: 'Kunst & Basteln', icon: <BrushIcon fontSize="small" /> },
      { key: 'storage', label: 'Aufbewahrung für Sammlerstücke', icon: <InventoryIcon fontSize="small" /> },
      { key: 'game_accessories', label: 'Spielzubehör', icon: <GamesIcon fontSize="small" /> },
    ],
    rightByKey: {
      cards: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Boosterpacks' },
            { label: 'Kartendecks' },
            { label: 'Ungeschnittene Kartenbögen' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Einzelne Sammelkarten' },
            { label: 'Blaster-Boxen' },
            { label: 'Sammelkarten-Lots' },
          ],
        },
      ],
      game_accessories: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Spielsteine' },
            { label: 'Sonstiges Spielzubehör' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Würfel' },
            { label: 'Spielmatten' },
          ],
        },
      ],
      storage: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Aufbewahrungsboxen für Sammlerstücke' },
            { label: 'Kartenhalter' },
            { label: 'Trenner für Alben & Mappen' },
            { label: 'Puzzlematten' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Alben & Mappen' },
            { label: 'Kartenhüllen' },
            { label: 'Deck-Boxen' },
            { label: 'Nachfüllseiten für Alben & Mappen' },
            { label: 'Sonstige Aufbewahrung für Sammlerstücke' },
          ],
        },
      ],
      crafts: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Malerei' },
            { label: 'Kalligraphie' },
            { label: 'Papiermodellbau' },
            { label: 'Kerzenherstellung' },
            { label: 'Bastelbedarf' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Nähen, Stricken & Handarbeit' },
            { label: 'Zeichnen & Skizzieren' },
            { label: 'Schmuckherstellung' },
            { label: 'Stanzen' },
            { label: 'Bildhauerei & Töpfern' },
            { label: 'Bastelwerkzeuge' },
          ],
        },
      ],
      music_instruments: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Verstärker & Effektpedale' },
            { label: 'Keyboards & Synthesizer' },
            { label: 'Blasinstrumente' },
            { label: 'DJ-Ausrüstung' },
            { label: 'Zubehör zum Musikmachen' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Gitarren & Bassgitarren' },
            { label: 'Trommeln & Percussion' },
            { label: 'Saiteninstrumente' },
            { label: 'Studio- und Live-Sound-Ausstattung' },
            { label: 'Karaoke-Ausrüstung' },
          ],
        },
      ],
      stamps: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Briefmarkensammlungen & -sets' },
            { label: 'Briefmarkenkataloge & -Fachliteratur' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Einzelne Briefmarken' },
            { label: 'First-Day-Cover' },
            { label: 'Zubehör für Briefmarkensammlungen' },
          ],
        },
      ],
      coins: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Münzen' },
            { label: 'Medaillen & Marken' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Banknoten' },
            { label: 'Lots & Sets' },
            { label: 'Wertpapiere' },
          ],
        },
      ],
      memorabilia: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Musik-Sammlerstücke' },
            { label: 'Sonstige Sammlerstücke' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Sport-Sammlerstücke' },
            { label: 'Film- & TV-Sammlerstücke' },
          ],
        },
      ],
    },
    right: [
      {
        title: 'Alle Kategorien',
        items: [
          { label: 'Sammelkarten' },
          { label: 'Brettspiele' },
          { label: 'Puzzles' },
          { label: 'Tabletop- & Miniaturspiele' },
        ],
      },
    ],
  },
  Traditionell: {
    left: [
      { key: 'all', label: 'Alle anzeigen', icon: <AllIcon fontSize="small" /> },
      { key: 'muslim_clothing', label: 'Islamische Kleidung', icon: <ClothingIcon fontSize="small" /> },
      { key: 'muslim_accessories', label: 'Islamisches Zubehör', icon: <InventoryIcon fontSize="small" /> },
      { key: 'islamic_books', label: 'Islamische Bücher', icon: <LibraryIcon fontSize="small" /> },
      { key: 'ramadan_eid', label: 'Ramadan & Eid', icon: <CelebrationIcon fontSize="small" /> },
      { key: 'christians', label: 'Christlich', icon: <StyleIcon fontSize="small" /> },
      { key: 'christian_clothing', label: 'Christliche Kleidung', icon: <ClothingIcon fontSize="small" /> },
      { key: 'orthodox', label: 'Orthodox', icon: <CollectionsBookmarkIcon fontSize="small" /> },
      { key: 'orthodox_clothing', label: 'Orthodoxe Kleidung', icon: <ClothingIcon fontSize="small" /> },
      { key: 'jewish', label: 'Jüdisch', icon: <StyleIcon fontSize="small" /> },
      { key: 'jewish_clothing', label: 'Jüdische Kleidung', icon: <ClothingIcon fontSize="small" /> },
      { key: 'hindu', label: 'Hindu', icon: <CollectionsBookmarkIcon fontSize="small" /> },
      { key: 'hindu_clothing', label: 'Hindu-Kleidung', icon: <ClothingIcon fontSize="small" /> },
      { key: 'buddhist', label: 'Buddhistisch', icon: <LibraryIcon fontSize="small" /> },
      { key: 'buddhist_clothing', label: 'Buddhistische Kleidung', icon: <ClothingIcon fontSize="small" /> },
      { key: 'others', label: 'Weitere Traditionen', icon: <OtherIcon fontSize="small" /> },
    ],
    rightByKey: {
      muslim_clothing: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Abayas' },
            { label: 'Hijabs & Khimars' },
            { label: 'Jilbabs & Niqabs' },
            { label: 'Thobes / Jalabiyas' },
            { label: 'Unterkleidung & Modesty-Wear' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Prayer-Outfits' },
            { label: 'Hijab-Pins & -Magnete' },
            { label: 'Kufi / Taqiyah' },
            { label: 'Kinder – Islamische Kleidung' },
            { label: 'Festliche Kleidung (Eid)' },
          ],
        },
      ],
      muslim_accessories: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Gebetsteppiche' },
            { label: 'Misbaha / Tasbih (Gebetsketten)' },
            { label: 'Quran-Ständer (Rehal)' },
            { label: 'Islamische Deko' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Attar / Parfümöle (alkoholfrei)' },
            { label: 'Gebetskappen (Kufi / Taqiyah)' },
            { label: 'Hajj & Umrah-Zubehör' },
            { label: 'Zamzam & Geschenke' },
          ],
        },
      ],
      islamic_books: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Qur’an' },
            { label: 'Tafsir' },
            { label: 'Hadith' },
            { label: 'Fiqh & Aqidah' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Biographien' },
            { label: 'Kinderbücher (Islam)' },
            { label: 'Arabisch lernen' },
            { label: 'Duas & Gebet' },
          ],
        },
      ],
      ramadan_eid: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Ramadan-Deko & Laternen' },
            { label: 'Eid-Deko & Banner' },
            { label: 'Geschenkboxen & -tüten' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Datteln & Süßwaren-Geschenke' },
            { label: 'Tischdeko & Servierartikel' },
            { label: 'Kinder-Activities (Ramadan/Eid)' },
          ],
        },
      ],
      christians: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Rosenkränze' },
            { label: 'Kreuze & Kruzifixe' },
            { label: 'Heiligenbilder' },
            { label: 'Bibel & Andachtsbücher' },
            { label: 'Geschenkartikel' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Kirchliche Deko' },
            { label: 'Kerzen & Weihwassergefäße' },
            { label: 'Liturgische Kleidung' },
            { label: 'Messgewänder & Stolen' },
            { label: 'Ministrantenkleidung' },
          ],
        },
      ],
      orthodox: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Ikonen' },
            { label: 'Weihrauch & Weihrauchgefäße' },
            { label: 'Gebetsschnüre (Komboskini)' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Ölgefäße & Dochte' },
            { label: 'Kirchliche Textilien' },
            { label: 'Orthodoxe Bücher' },
            { label: 'Liturgische Kleidung' },
            { label: 'Weihrauch-Kohle & Zubehör' },
          ],
        },
      ],
      jewish: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Kippa' },
            { label: 'Tallit & Tzitzit' },
            { label: 'Mezuzah' },
            { label: 'Menora' },
            { label: 'Kiddusch-Becher' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Schabbat-Artikel' },
            { label: 'Siddur & Gebetbücher' },
            { label: 'Deko & Wandkunst' },
            { label: 'Koscher-Küchen-Artikel' },
            { label: 'Geschenksets' },
          ],
        },
      ],
      hindu: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Pooja-Artikel' },
            { label: 'Diyas / Öllampen' },
            { label: 'Räucherstäbchen & Halter' },
            { label: 'Idole & Murti' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Mandir-Zubehör' },
            { label: 'Festdeko (Diwali/Navratri)' },
            { label: 'Kumkum & Tilak' },
            { label: 'Malas' },
            { label: 'Geschenksets' },
          ],
        },
      ],
      buddhist: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Gebetsfahnen' },
            { label: 'Malas' },
            { label: 'Räucherstäbchen & Halter' },
            { label: 'Buddha-Statuen' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Klangschalen' },
            { label: 'Thangka-Kunst' },
            { label: 'Meditationskissen & Matten' },
            { label: 'Bücher & Lehren' },
          ],
        },
      ],
      christian_clothing: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Messgewänder' },
            { label: 'Chorhemden / Alben' },
            { label: 'Ministrantenkleidung' },
            { label: 'Ordenskleidung' },
            { label: 'Kopfbedeckungen' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Stolen & Schärpen' },
            { label: 'Talare' },
            { label: 'Gewänder für Anlässe' },
            { label: 'Zubehör' },
          ],
        },
      ],
      orthodox_clothing: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Rasson / Kassock' },
            { label: 'Sticharion' },
            { label: 'Epitrachelion' },
            { label: 'Phelonion' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Kopf- & Schultertücher' },
            { label: 'Mönchs-/Nonnenkleidung' },
            { label: 'Liturgische Gürtel' },
            { label: 'Zubehör' },
          ],
        },
      ],
      jewish_clothing: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Tzniut-Kleider & Röcke' },
            { label: 'Tichel / Kopftücher' },
            { label: 'Sheitel / Perücken' },
            { label: 'Kittel' },
            { label: 'Tallit Katan' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Kippot' },
            { label: 'Bekeshe & Kaftan' },
            { label: 'Schabbat- & Festkleidung' },
            { label: 'Kinderkleidung' },
            { label: 'Zubehör' },
          ],
        },
      ],
      hindu_clothing: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Sarees' },
            { label: 'Salwar Kameez' },
            { label: 'Lehenga Choli' },
            { label: 'Kurtas' },
            { label: 'Sherwani' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Dhoti / Veshti' },
            { label: 'Dupattas & Schals' },
            { label: 'Pooja-Kleidung' },
            { label: 'Kinderkleidung' },
            { label: 'Zubehör' },
          ],
        },
      ],
      buddhist_clothing: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Roben (Kasaya/Kesa)' },
            { label: 'Meditationskleidung' },
            { label: 'Mönchs-/Nonnenkleidung' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Shawls & Schultertücher' },
            { label: 'Ritualkleidung' },
            { label: 'Zubehör' },
          ],
        },
      ],
      others: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Jüdisch (Kippa, Menora, Mezuzah)' },
            { label: 'Hindu (Öllampen, Pooja-Artikel)' },
            { label: 'Buddhistisch (Gebetsfahnen, Malas)' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Traditionelle Kleidung – Sonstiges' },
            { label: 'Deko & Kunsthandwerk' },
            { label: 'Geschenke & Sets' },
          ],
        },
      ],
    },
    right: [
      {
        title: 'Alle Kategorien',
        items: [
          { label: 'Islamische Kleidung' },
          { label: 'Islamisches Zubehör' },
          { label: 'Islamische Bücher' },
          { label: 'Ramadan & Eid' },
          { label: 'Christlich' },
          { label: 'Orthodox' },
          { label: 'Weitere Traditionen' },
        ],
      },
    ],
  },
  Sport: {
    left: [
      { key: 'all', label: 'Alle anzeigen', icon: <AllIcon fontSize="small" /> },
      { key: 'cycling', label: 'Fahrradfahren', icon: <BikeIcon fontSize="small" /> },
      { key: 'fitness', label: 'Fitness, Laufen & Yoga', icon: <FitnessIcon fontSize="small" /> },
      { key: 'outdoor', label: 'Outdoor-Sport', icon: <HikingIcon fontSize="small" /> },
      { key: 'water', label: 'Wassersport', icon: <KayakingIcon fontSize="small" /> },
      { key: 'team', label: 'Teamsport', icon: <SoccerIcon fontSize="small" /> },
      { key: 'racket', label: 'Schlägersportarten', icon: <TennisIcon fontSize="small" /> },
      { key: 'golf', label: 'Golf', icon: <GolfIcon fontSize="small" /> },
      { key: 'equestrian', label: 'Reitsport', icon: <HorseIcon fontSize="small" /> },
      { key: 'skate', label: 'Skateboards & Roller', icon: <SkateboardingIcon fontSize="small" /> },
      { key: 'boxing', label: 'Boxen & Kampfsport', icon: <MmaIcon fontSize="small" /> },
      { key: 'leisure', label: 'Freizeitsport & -spiele', icon: <BaseballIcon fontSize="small" /> },
      { key: 'winter', label: 'Wintersport', icon: <SkiIcon fontSize="small" /> },
    ],
    rightByKey: {
      cycling: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Fahrradhelme' },
            { label: 'Fahrradanhänger' },
            { label: 'Fahrrad-Ersatzteile' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Räder für Kinder' },
            { label: 'Fahrradzubehör & -werkzeuge' },
            { label: 'Fahrradsitze für Kinder' },
          ],
        },
      ],
      water: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Aufblasbare Flöße' },
            { label: 'Kiteboards' },
            { label: 'Stand-up-Paddleboards' },
            { label: 'Wakeboards' },
            { label: 'Schwimmhilfen' },
            { label: 'Neoprenanzüge, -handschuhe & -füßlinge' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Schwimmen' },
            { label: 'Kayaks' },
            { label: 'Skimboards' },
            { label: 'Schleppreifen' },
            { label: 'Wasserskier' },
            { label: 'Wassersport-Helme' },
            { label: 'Wassersport-Zubehör' },
          ],
        },
      ],
      outdoor: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Campingzelte & -schlafzubehör' },
            { label: 'Ferngläser & Fernrohre' },
            { label: 'Trinksysteme & -rucksäcke' },
            { label: 'Taschenlampen, Stirnlampen & Laternen' },
            { label: 'Kompasse' },
            { label: 'Angeln & Jagen' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Klettern & Bouldern' },
            { label: 'Camping-Kocher & -Kochgeschirr' },
            { label: 'Kühlboxen & -taschen' },
            { label: 'Camping-Möbel' },
            { label: 'Wanderstöcke' },
            { label: 'Wanderrucksäcke' },
            { label: 'Anderes Zubehör für den Outdoor-Sport' },
          ],
        },
      ],
      fitness: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Laufen' },
            { label: 'Fitnesszubehör für Zuhause' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Krafttraining' },
            { label: 'Yoga- & Pilates-Ausrüstung' },
            { label: 'Wasserflaschen' },
          ],
        },
      ],
      racket: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Squash' },
            { label: 'Racquetball' },
            { label: 'Padel' },
            { label: 'Augenschutz für den Schlägersport' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Tennis' },
            { label: 'Badminton' },
            { label: 'Tischtennis' },
            { label: 'Pickleball' },
          ],
        },
      ],
      winter: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Snowboard-Ausrüstung' },
            { label: 'Eiskunstlauf-Zubehör' },
            { label: 'Schneeschuhe' },
            { label: 'Skibrillen' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Ski-Ausrüstung' },
            { label: 'Eishockey' },
            { label: 'Schlittenfahren' },
            { label: 'Gamaschen' },
            { label: 'Wintersporthelme' },
          ],
        },
      ],
      boxing: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Körperschutz für Box- & Kampfsport' },
            { label: 'Handschuhe für Box- & Kampfsport' },
            { label: 'Kampfsportgürtel' },
            { label: 'Speed Bags' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Kopfschutz für Box- & Kampfsport' },
            { label: 'Schlag- & Trittpolster' },
            { label: 'Handbandagen' },
            { label: 'Boxsäcke' },
            { label: 'Andere Kampfsportausrüstung' },
          ],
        },
      ],
      skate: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Skateboards' },
            { label: 'Skate-Helme' },
            { label: 'Skate-Ersatzteile & Zubehör' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Longboards' },
            { label: 'Roller' },
            { label: 'Skate-Schutzausrüstung' },
            { label: 'Skateboard-Ersatzteile & Zubehör' },
          ],
        },
      ],
      equestrian: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Reithandschuhe' },
            { label: 'Reithelm-Überzüge' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Reitsport-Schutzausrüstung' },
            { label: 'Reithelme' },
            { label: 'Sättel & Reitzubehör' },
          ],
        },
      ],
      golf: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Golftaschen' },
            { label: 'Golfschläger' },
            { label: 'Golftrolleys' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Golfzubehör' },
            { label: 'Golfbälle' },
            { label: 'Golfhandschuhe' },
            { label: 'Ausstattung fürs Golftraining' },
          ],
        },
      ],
      team: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Basketball' },
            { label: 'Rugby' },
            { label: 'American Football' },
            { label: 'Handball' },
            { label: 'Feldhockey' },
            { label: 'Gälische Sportarten' },
            { label: 'Netball' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Fußball' },
            { label: 'Volleyball' },
            { label: 'Trainings- und Schiedsrichterausrüstung' },
            { label: 'Baseball & Softball' },
            { label: 'Cricket' },
            { label: 'Unihockey' },
            { label: 'Lacrosse' },
            { label: 'Andere Teamsport-Ausrüstung' },
          ],
        },
      ],
      leisure: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Dart-Ausrüstung' },
            { label: 'Spielplatzbälle' },
            { label: 'Roundnet & Spikeball' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Boule & andere Spiele' },
            { label: 'Wurfscheiben & Discgolf' },
            { label: 'Pool & Snooker' },
            { label: 'Bowling' },
          ],
        },
      ],
    },
    right: [
      {
        title: 'Alle Kategorien',
        items: [
          { label: 'Fahrradfahren' },
          { label: 'Fitness, Laufen & Yoga' },
          { label: 'Outdoor-Sport' },
        ],
      },
    ],
  },
  Home: {
    left: [
      { key: 'all', label: 'Alle anzeigen', icon: <AllIcon fontSize="small" /> },
      { key: 'small_appliances', label: 'Kleine Küchengeräte', icon: <KitchenIcon fontSize="small" /> },
      { key: 'cook_bake', label: 'Koch- und Backutensilien', icon: <BakeryIcon fontSize="small" /> },
      { key: 'kitchen_helpers', label: 'Küchenhelfer', icon: <RestaurantIcon fontSize="small" /> },
      { key: 'food', label: 'Essen', icon: <FastfoodIcon fontSize="small" /> },
      { key: 'household', label: 'Haushaltsgeräte', icon: <ElectricalIcon fontSize="small" /> },
      { key: 'textiles', label: 'Textilien', icon: <DryCleaningIcon fontSize="small" /> },
      { key: 'decor', label: 'Wohnaccessoires', icon: <HomeHouseIcon fontSize="small" /> },
      { key: 'office', label: 'Büromaterial', icon: <InventoryIcon fontSize="small" /> },
      { key: 'events', label: 'Feste & Feiertage', icon: <CelebrationIcon fontSize="small" /> },
      { key: 'diy', label: 'Werkzeuge & Heimwerken', icon: <BuildIcon fontSize="small" /> },
      { key: 'garden', label: 'Außenbereich & Garten', icon: <YardIcon fontSize="small" /> },
      { key: 'pets', label: 'Haustierbedarf', icon: <PetsIcon fontSize="small" /> },
    ],
    rightByKey: {
      small_appliances: [
        {
          title: 'Kleine Küchengeräte',
          items: [
            { label: 'Alle anzeigen' },
            { label: 'Zubereitung von Kaffee, Tee und Espresso' },
            { label: 'Mixer und Küchenmaschinen' },
            { label: 'Fritteusen' },
            { label: 'Elektrische Grills und Grillplatten' },
            { label: 'Wasser- und Getränkespender' },
            { label: 'Zubehör für kleine Küchengeräte' },
          ],
        },
        {
          title: 'Weitere Geräte',
          items: [
            { label: 'Kessel und Wasserkocher' },
            { label: 'Toaster' },
            { label: 'Mikrowellen' },
            { label: 'Kochplatten' },
            { label: 'Entsafter' },
            { label: 'Spezialgeräte' },
            { label: 'Ersatzteile für kleine Küchengeräte' },
          ],
        },
      ],
      pets: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Katzen' },
            { label: 'Fische' },
            { label: 'Reptilien' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Hunde' },
            { label: 'Kleintiere' },
            { label: 'Vögel' },
          ],
        },
      ],
      garden: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Zubehör für Elektro-Gartengeräte' },
            { label: 'Töpfe, Pflanzgefäße & Zubehör' },
            { label: 'Außen- & Gartendeko' },
            { label: 'Whirlpools, Pools & Zubehör' },
            { label: 'Schneeräumgeräte' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Elektrowerkzeuge für den Außenbereich' },
            { label: 'Manuelle Gartengeräte' },
            { label: 'Bewässerungszubehör' },
            { label: 'Kochen & Grillen im Freien' },
            { label: 'Wetterinstrumente' },
          ],
        },
      ],
      diy: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Handwerkzeuge' },
            { label: 'Malerbedarf' },
            { label: 'Elektrikerbedarf' },
            { label: 'Werkzeugzubehör' },
            { label: 'Werkzeugaufbewahrung & -transport' },
            { label: 'Eisenwaren' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Elektrowerkzeuge' },
            { label: 'Messwerkzeuge' },
            { label: 'Sanitärwerkzeuge' },
            { label: 'Maurerwerkzeuge' },
            { label: 'Schutzausrüstung' },
            { label: 'Werkstatt- & Baustellenausrüstung' },
            { label: 'Smart Home & Sicherheit' },
          ],
        },
      ],
      events: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Karten & Umschläge' },
            { label: 'Saisonale Dekoration' },
            { label: 'Tischdeko' },
            { label: 'Kränze' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Spruchbänder, Fahnen & Wimpel' },
            { label: 'Geschenkpapier & -tüten' },
            { label: 'Partydeko' },
            { label: 'Baumschmuck' },
          ],
        },
      ],
      office: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Notizbücher & Schreibblöcke' },
            { label: 'Lesezeichen' },
            { label: 'Schreibtischzubehör' },
            { label: 'Schreibzeug' },
            { label: 'Klebeband, Clips & Befestigungselemente' },
            { label: 'Präsentationsmaterialien' },
            { label: 'Tresore' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Planer & persönliche Organizer' },
            { label: 'Etuis' },
            { label: 'Taschenrechner' },
            { label: 'Dokumenten-Organizer' },
            { label: 'Werkzeuge für technisches Zeichnen' },
            { label: 'Heftgeräte & Locher' },
            { label: 'Elektronikgeräte fürs Büro' },
          ],
        },
      ],
      decor: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Uhren' },
            { label: 'Dekoratives Zubehör' },
            { label: 'Kaminzubehör' },
            { label: 'Wandregale & -boards' },
            { label: 'Spiegel' },
            { label: 'Vasen' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Kerzen & Raumdüfte' },
            { label: 'Skulpturen & Statuen' },
            { label: 'Künstliche Pflanzen & Blumen' },
            { label: 'Beleuchtung' },
            { label: 'Rahmen' },
            { label: 'Aufbewahrung & Organisation' },
            { label: 'Wanddekor' },
          ],
        },
      ],
      textiles: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Decken' },
            { label: 'Kissen' },
            { label: 'Matten & Teppiche' },
            { label: 'Wandteppiche' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Bettzeug' },
            { label: 'Vorhänge & Jalousien' },
            { label: 'Schonbezüge' },
            { label: 'Tischdecken' },
            { label: 'Tücher' },
          ],
        },
      ],
      household: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Bügeln und Kleiderpflege' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Heizung, Kühlung und Belüftung' },
            { label: 'Staubsaugen und Reinigen' },
          ],
        },
      ],
      food: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Essgeschirr' },
            { label: 'Servierbedarf' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Geschirr' },
            { label: 'Tassen, Gläser & Kannen' },
          ],
        },
      ],
      kitchen_helpers: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Kochutensilien' },
            { label: 'Messbecher und -löffel' },
            { label: 'Rührschüsseln' },
            { label: 'Aufbewahrung von Lebensmitteln' },
            { label: 'Spezielle Küchengeräte' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Schneidebretter' },
            { label: 'Küchenwaagen' },
            { label: 'Lebensmittelthermometer' },
            { label: 'Küchensiebe' },
            { label: 'Barkeeper-Utensilien' },
          ],
        },
      ],
      cook_bake: [
        {
          title: 'Alle anzeigen',
          items: [
            { label: 'Pfannen' },
            { label: 'Utensilien zum Backen und Braten' },
            { label: 'Spezielle Koch- und Backutensilien' },
            { label: 'Zubehör für Koch- und Backutensilien' },
          ],
        },
        {
          title: ' ',
          items: [
            { label: 'Töpfe' },
            { label: 'Backbleche' },
            { label: 'Backformen' },
            { label: 'Koch- und Backsets' },
          ],
        },
      ],
    },
    right: [
      {
        title: 'Alle Kategorien',
        items: [
          { label: 'Kleine Küchengeräte' },
          { label: 'Koch- und Backutensilien' },
          { label: 'Küchenhelfer' },
        ],
      },
      {
        title: 'Mehr',
        items: [
          { label: 'Haushaltsgeräte' },
          { label: 'Textilien' },
          { label: 'Wohnaccessoires' },
        ],
      },
    ],
  },
  default: {
    left: [
      { key: 'all', label: 'Alle anzeigen', icon: <AllIcon fontSize="small" /> },
      { key: 'clothing', label: 'Bekleidung', icon: <ClothingIcon fontSize="small" /> },
      { key: 'sport', label: 'Sport', icon: <SportIcon fontSize="small" /> },
      { key: 'designer', label: 'Designer', icon: <DesignerIcon fontSize="small" /> },
      { key: 'other', label: 'Weitere', icon: <OtherIcon fontSize="small" /> },
    ],
    right: [
      { title: 'Beliebt', items: [ { label: 'Neuheiten' }, { label: 'Angebote' }, { label: 'Bestseller' } ] },
      { title: 'Kategorien', items: [ { label: 'Oberteile' }, { label: 'Hosen' }, { label: 'Kleider' } ] },
      { title: 'Accessoires', items: [ { label: 'Taschen' }, { label: 'Schmuck' }, { label: 'Gürtel' } ] },
    ],
  },
};

export default MegaMenu;
